import React from 'react';
import { PublicLayout } from '../../components/PublicLayout';
import { PageHero, BenefitsGrid, HowItWorks, FAQSection, CTASection } from '../../components/PageTemplate';
import { 
  Home, PenTool, CreditCard, MessageCircle, Wrench, 
  FileText, Calendar, Shield 
} from 'lucide-react';

export default function TenantsJourney() {
  const phases = [
    {
      title: 'Move In',
      description: 'Complete check-in inventory via the app, sign for keys, and settle into your new home.'
    },
    {
      title: 'Set Up Payments',
      description: 'Direct Debit is set up for automatic rent collection each month.'
    },
    {
      title: 'Report Issues',
      description: 'Any maintenance problems? Report through the app with photos for fast resolution.'
    },
    {
      title: 'Inspections',
      description: 'Periodic inspections via the app - take photos on your schedule, no awkward visits.'
    },
    {
      title: 'Renewal Time',
      description: 'Before your tenancy ends, we\'ll discuss renewal or your next steps.'
    },
    {
      title: 'Move Out',
      description: 'Check-out inspection, deposit return process, and handover keys.'
    }
  ];

  const features = [
    {
      icon: CreditCard,
      title: 'Automated Rent',
      description: 'Direct Debit means rent is paid automatically. No remembering dates.'
    },
    {
      icon: MessageCircle,
      title: 'Direct Messaging',
      description: 'Communicate with your landlord or property manager directly in the app.'
    },
    {
      icon: Wrench,
      title: 'Repair Requests',
      description: 'Report issues with photos. Track progress until the job is complete.'
    },
    {
      icon: FileText,
      title: 'Documents Access',
      description: 'Your tenancy agreement, safety certificates, and receipts - all in one place.'
    },
    {
      icon: Calendar,
      title: 'Inspection Scheduling',
      description: 'Flexible inspection slots. Complete on your schedule when possible.'
    },
    {
      icon: Shield,
      title: 'Deposit Protection',
      description: 'Your deposit is protected. View status and get clear information about returns.'
    }
  ];

  const faqs = [
    {
      question: 'How do I pay my rent?',
      answer: 'Rent is collected by Direct Debit on the 1st of each month (or your agreed date). You set this up during onboarding, and it happens automatically.'
    },
    {
      question: 'What if I have a maintenance issue?',
      answer: 'Open the app, tap "Report Issue", describe the problem, add photos, and submit. You\'ll get updates as the repair progresses.'
    },
    {
      question: 'How do inspections work?',
      answer: 'You\'ll be notified when an inspection is due. Where possible, you can complete it yourself by taking photos through the app. Some inspections may require an in-person visit.'
    },
    {
      question: 'Can I request repairs at any time?',
      answer: 'Yes. Submit requests 24/7 through the app. Emergencies (no heating, leaks, security issues) are flagged as urgent and prioritized.'
    },
    {
      question: 'What happens when my tenancy is ending?',
      answer: 'We\'ll contact you 2 months before the end date to discuss renewal or move-out. If moving out, we\'ll guide you through the check-out process.'
    }
  ];

  return (
    <PublicLayout>
      <PageHero
        badge="TENANCY JOURNEY"
        title="Your Home,"
        subtitle="Your Way"
        description="From move-in to move-out, everything you need to manage your tenancy is in one place. Payments, repairs, documents, communication."
        primaryCTA={{ href: '/register', label: 'Get Started' }}
        secondaryCTA={{ href: '/tenants', label: 'Back to Overview' }}
        gradient="from-violet-50 via-white to-slate-50"
      />

      <HowItWorks title="Your Tenancy Timeline" steps={phases} />

      <BenefitsGrid title="Tenant Features" benefits={features} />

      {/* App Preview */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-display font-bold mb-4">
            Everything in Your Pocket
          </h2>
          <p className="text-slate-600 mb-8 max-w-2xl mx-auto">
            The LetWise app puts your entire tenancy at your fingertips. Report repairs, check payment history, access documents, and communicate with your landlord - all from your phone.
          </p>
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="p-6 bg-slate-50 rounded-xl">
              <div className="text-3xl mb-3">💱</div>
              <h3 className="font-semibold mb-2">Payments</h3>
              <p className="text-sm text-slate-600">View history, receipts, and upcoming charges</p>
            </div>
            <div className="p-6 bg-slate-50 rounded-xl">
              <div className="text-3xl mb-3">🔧</div>
              <h3 className="font-semibold mb-2">Repairs</h3>
              <p className="text-sm text-slate-600">Report issues and track repair progress</p>
            </div>
            <div className="p-6 bg-slate-50 rounded-xl">
              <div className="text-3xl mb-3">📄</div>
              <h3 className="font-semibold mb-2">Documents</h3>
              <p className="text-sm text-slate-600">Access your contract and safety certificates</p>
            </div>
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />

      <CTASection
        title="Ready to experience better renting?"
        description="Create your profile and find your next home with LetWise."
        primaryCTA={{ href: '/register', label: 'Create Profile' }}
        secondaryCTA={{ href: '/properties', label: 'Browse Properties' }}
      />
    </PublicLayout>
  );
}
